import React, { useRef, useState, useEffect } from 'react';
import { LogIn, Sun, Moon, Sparkles, LayoutTemplate, Shield, Download, CheckCircle2 } from 'lucide-react';
import { motion, useScroll, useTransform } from 'motion/react';
import { cn } from '../lib/utils';
import { THEMES, PATTERNS, BADGES } from '../data';
import { BannerPreview } from './BannerPreview';

interface LandingPageProps {
  onSignIn: () => void;
  isDarkMode: boolean;
  toggleDarkMode: () => void;
}

export function LandingPage({ onSignIn, isDarkMode, toggleDarkMode }: LandingPageProps) {
  // Sample data for preview banners
  const sampleBadges = BADGES.slice(0, 4);

  const container1Ref = useRef<HTMLDivElement>(null);
  const container2Ref = useRef<HTMLDivElement>(null);
  const container3Ref = useRef<HTMLDivElement>(null);
  const container4Ref = useRef<HTMLDivElement>(null);
  const [scale1, setScale1] = useState(1);
  const [scale2, setScale2] = useState(1);
  const [scale3, setScale3] = useState(1);
  const [scale4, setScale4] = useState(1);

  useEffect(() => {
    const updateScales = () => {
      if (container1Ref.current) {
        const w = container1Ref.current.clientWidth;
        setScale1(w / 1584);
      }
      if (container2Ref.current) {
        const w = container2Ref.current.clientWidth;
        setScale2(w / 1584);
      }
      if (container3Ref.current) {
        const w = container3Ref.current.clientWidth;
        setScale3(w / 1080);
      }
      if (container4Ref.current) {
        const w = container4Ref.current.clientWidth;
        setScale4(w / 1080);
      }
    };

    updateScales();
    const observer = new ResizeObserver(updateScales);
    if (container1Ref.current) observer.observe(container1Ref.current);
    if (container2Ref.current) observer.observe(container2Ref.current);
    if (container3Ref.current) observer.observe(container3Ref.current);
    if (container4Ref.current) observer.observe(container4Ref.current);

    window.addEventListener('resize', updateScales);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateScales);
    };
  }, []);

  const features = [
    {
      icon: <LayoutTemplate className="w-6 h-6" />,
      title: "Multiple Layouts",
      description: "Choose from horizontal, vertical, or square banners designed for LinkedIn, Twitter, and GitHub."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Google Cloud Credentials",
      description: "Showcase your official Google Cloud credentials and badges to stand out to recruiters."
    },
    {
      icon: <Download className="w-6 h-6" />,
      title: "High-Res Export",
      description: "Download your banners in ultra-high resolution ready to be uploaded to your social profiles."
    }
  ];

  return (
    <div className={cn(
      "min-h-screen flex flex-col font-sans transition-colors duration-500 overflow-hidden relative",
      isDarkMode ? "dark bg-zinc-950 text-white" : "bg-zinc-50 text-zinc-900"
    )}>
      {/* Background decorations */}
      <div className="absolute top-0 inset-x-0 h-full overflow-hidden -z-10 pointer-events-none">
        <motion.div 
          animate={{ 
            scale: [1, 1.1, 1],
            opacity: [0.3, 0.4, 0.3],
          }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          className={cn(
            "absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full blur-[120px] mix-blend-screen transition-colors duration-1000",
            isDarkMode ? "bg-indigo-600" : "bg-blue-300"
          )} 
        />
        <motion.div 
          animate={{ 
            scale: [1, 1.2, 1],
            opacity: [0.2, 0.3, 0.2],
          }}
          transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
          className={cn(
            "absolute top-[20%] -right-[10%] w-[40%] h-[60%] rounded-full blur-[120px] mix-blend-screen transition-colors duration-1000",
            isDarkMode ? "bg-fuchsia-600" : "bg-purple-300"
          )} 
        />
      </div>

      <nav className="relative z-50 flex justify-between items-center px-6 py-6 max-w-7xl mx-auto w-full">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Orbit of Ops" className="w-10 h-10 rounded-xl object-cover shadow-sm" />
          <span className="font-extrabold text-xl tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>Prism Canvas <span className="text-sm font-medium text-white/70">by orbitofops</span></span>
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={toggleDarkMode}
            className="p-2.5 bg-white/50 dark:bg-zinc-900/50 backdrop-blur-md text-zinc-600 dark:text-zinc-400 hover:bg-white dark:hover:bg-zinc-800 rounded-xl transition-colors border border-zinc-200 dark:border-zinc-800 shadow-sm"
          >
            {isDarkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
          <button 
            onClick={onSignIn}
            className="px-4 py-2 text-sm font-bold bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-lg hover:bg-zinc-800 dark:hover:bg-zinc-100 transition-colors shadow-sm hidden sm:block"
          >
            Sign In
          </button>
        </div>
      </nav>

      <main className="flex-1 max-w-7xl mx-auto w-full px-6 py-12 md:py-20 flex flex-col items-center justify-center relative z-10">
        
        {/* Hero Content */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="text-center max-w-4xl mx-auto mb-20 space-y-8"
        >
          <motion.div 
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 font-medium text-sm border border-indigo-100 dark:border-indigo-500/20 mb-4 shadow-sm"
          >
            <Sparkles className="w-4 h-4" />
            <span>Discover the new standard for professional branding</span>
          </motion.div>
          
          <h1 className="font-extrabold text-6xl md:text-8xl tracking-tight leading-[1.05] mb-6" style={{ fontFamily: 'var(--font-display)' }}>
            Elevate your <br className="hidden md:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-500 to-fuchsia-500 dark:from-indigo-400 dark:to-fuchsia-400">
              Cloud Identity
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-3xl mx-auto font-medium">
            Design stunning, personalized banners for LinkedIn, Twitter, and GitHub. Showcase your certifications, skills, and achievements in seconds. Create your own for free.
          </p>
          
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="pt-8 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <button 
              onClick={onSignIn}
              className="w-full sm:w-auto px-8 py-4 bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 hover:bg-black dark:hover:bg-zinc-100 font-bold rounded-2xl flex items-center justify-center gap-3 transition-all shadow-xl hover:shadow-2xl hover:-translate-y-1 active:translate-y-0 text-lg"
            >
              <LogIn className="w-5 h-5" />
              Get Started for Free
            </button>
          </motion.div>
        </motion.div>

        {/* Feature Highlights */}
        <motion.div 
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-32 w-full max-w-5xl"
        >
          {features.map((feature, i) => (
            <motion.div 
              key={i}
              whileHover={{ y: -5 }}
              className="bg-white/50 dark:bg-zinc-900/50 backdrop-blur-sm p-8 rounded-3xl border border-zinc-200 dark:border-zinc-800 text-center"
            >
              <div className="w-14 h-14 mx-auto bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 rounded-2xl flex items-center justify-center mb-6">
                {feature.icon}
              </div>
              <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
              <p className="text-zinc-500 dark:text-zinc-400 leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Floating Previews */}
        <div id="examples" className="w-full flex flex-col gap-12 lg:gap-20 perspective-[2000px] pb-32">
          
          <div className="text-center mb-4">
            <h2 className="text-3xl md:text-5xl font-extrabold tracking-tight mb-4" style={{ fontFamily: 'var(--font-display)' }}>Built for every platform</h2>
            <p className="text-xl text-zinc-500 dark:text-zinc-400">Horizontal, square, and vertical formats to fit anywhere.</p>
          </div>

          <motion.div 
            initial={{ opacity: 0, rotateX: 10, y: 40 }}
            whileInView={{ opacity: 1, rotateX: 0, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16"
          >
            {/* Banner 1 */}
            <motion.div 
              whileHover={{ scale: 1.02, rotateY: 2, rotateX: 2 }}
              ref={container1Ref}
              className="transform-gpu transition-all duration-700 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10 bg-zinc-100 dark:bg-zinc-900" 
              style={{ 
                transformStyle: 'preserve-3d', 
                aspectRatio: '4/1'
              }}
            >
              <div className="relative w-full h-full">
                <BannerPreview
                  userName="Alex Chen"
                  userTitle="Senior Cloud Architect | Kubernetes Expert"
                  socialHandles={{ linkedin: 'alexchen-cloud', twitter: '@alexchen_tech' }}
                  selectedBadges={sampleBadges}
                  layout="compact"
                  fontFamily="sans"
                  theme={THEMES[0]} // Orbit Dark
                  pattern={PATTERNS[1]} // Grid
                  customImageUrl={undefined}
                  bannerSize="horizontal"
                  scale={scale1}
                />
              </div>
            </motion.div>
            
            {/* Banner 2 */}
            <motion.div 
              whileHover={{ scale: 1.02, rotateY: -2, rotateX: 2 }}
              ref={container2Ref}
              className="transform-gpu transition-all duration-700 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10 mt-0 md:mt-16 bg-zinc-100 dark:bg-zinc-900" 
              style={{ 
                transformStyle: 'preserve-3d', 
                aspectRatio: '4/1'
              }}
            >
              <div className="relative w-full h-full">
                <BannerPreview
                  userName="Sarah Jenkins"
                  userTitle="DevOps Engineer & Google Cloud Champion"
                  socialHandles={{ github: 'sarahj-dev', linkedin: 'sarah-jenkins' }}
                  selectedBadges={sampleBadges.slice(1, 4)}
                  layout="centric"
                  fontFamily="display"
                  theme={THEMES[2]} // Cyber
                  pattern={PATTERNS[3]} // Circuit
                  customImageUrl={undefined}
                  bannerSize="horizontal"
                  scale={scale2}
                />
              </div>
            </motion.div>
          </motion.div>
          
          <motion.div 
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 max-w-5xl mx-auto w-full"
          >
            {/* Banner 3 (Square) */}
            <motion.div 
              whileHover={{ scale: 1.02, rotateY: 2, rotateX: -2 }}
              ref={container3Ref}
              className="transform-gpu transition-all duration-700 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10 mx-auto w-full max-w-md bg-zinc-100 dark:bg-zinc-900" 
              style={{ 
                transformStyle: 'preserve-3d', 
                aspectRatio: '1/1'
              }}
            >
              <div className="relative w-full h-full">
                <BannerPreview
                  userName="David Park"
                  userTitle="Data Scientist & AI Researcher"
                  socialHandles={{ github: 'dpark-ai', linkedin: 'david-park-data' }}
                  selectedBadges={sampleBadges.slice(2, 4)}
                  layout="centric"
                  fontFamily="mono"
                  theme={THEMES[3]} // Hacker
                  pattern={PATTERNS[0]} // Dots
                  customImageUrl={undefined}
                  bannerSize="square"
                  scale={scale3}
                />
              </div>
            </motion.div>

            {/* Banner 4 (Vertical) */}
            <motion.div 
              whileHover={{ scale: 1.02, rotateY: -2, rotateX: -2 }}
              ref={container4Ref}
              className="transform-gpu transition-all duration-700 rounded-3xl overflow-hidden shadow-2xl ring-1 ring-black/5 dark:ring-white/10 mx-auto w-full max-w-[320px] mt-0 md:mt-16 bg-zinc-100 dark:bg-zinc-900" 
              style={{ 
                transformStyle: 'preserve-3d', 
                aspectRatio: '9/16'
              }}
            >
              <div className="relative w-full h-full">
                <BannerPreview
                  userName="Maya Lin"
                  userTitle="Frontend Developer & UI/UX Designer"
                  socialHandles={{ twitter: '@mayadesigns', linkedin: 'maya-lin-ux' }}
                  selectedBadges={sampleBadges.slice(0, 3)}
                  layout="compact"
                  fontFamily="sans"
                  theme={THEMES[1]} // Cloud Light
                  pattern={PATTERNS[4]} // Waves
                  customImageUrl={undefined}
                  bannerSize="vertical"
                  scale={scale4}
                />
              </div>
            </motion.div>
          </motion.div>
        </div>

        {/* CTA Section */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="w-full max-w-4xl mx-auto text-center bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 rounded-[3rem] p-12 md:p-20 shadow-2xl relative overflow-hidden"
        >
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
          </div>
          <div className="relative z-10 space-y-8">
            <h2 className="text-4xl md:text-5xl font-extrabold tracking-tight" style={{ fontFamily: 'var(--font-display)' }}>Ready to upgrade your profile?</h2>
            <p className="text-xl opacity-80 max-w-2xl mx-auto font-medium">Join thousands of professionals who are showcasing their skills with beautifully designed Prism Canvas banners by orbitofops.</p>
            <div className="pt-4 flex justify-center">
              <button 
                onClick={onSignIn}
                className="px-8 py-4 bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white font-bold rounded-2xl flex items-center justify-center gap-3 transition-all hover:scale-105 active:scale-95 shadow-xl text-lg"
              >
                <LogIn className="w-5 h-5" />
                Create your first banner
              </button>
            </div>
            <div className="flex items-center justify-center gap-6 text-sm font-medium opacity-70 pt-4">
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> 100% Free</div>
              <div className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> No credit card required</div>
            </div>
          </div>
        </motion.div>
        
      </main>

      {/* Footer */}
      <footer className="w-full py-8 text-center text-zinc-500 dark:text-zinc-400 font-medium text-sm mt-auto">
        <p>© {new Date().getFullYear()} Prism Canvas by orbitofops. Designed for Cloud Professionals.</p>
      </footer>
    </div>
  );
}

